import { ChangeDetectionStrategy, Component, computed, linkedSignal, signal } from "@angular/core";
import { Event } from "../../../pojo/Event";
import { Item } from "../../../pojo/Item";
import { CacheRepository } from "../../../decorators/cache.decorator";
import { AlertService } from "../../../services/alert.service";
import { AvailService } from "../../../services/avail.service";
import { EventService } from "../../../services/event.service";
import { ReservationService } from "../../../services/reservation.service";
import { UserprefService } from "../../../services/userpref.service";
import { TelemetryService } from "../../../services/telemetry.service";
import { S25Util } from "../../../util/s25-util";
import { OccurrenceTimeRangeLabels } from "../../../util/occurrence.time.colors";
import { s25Resource } from "../../../util/s25.resource";
import { S25ReservationUtil } from "../../s25-event/utils/s25.reservation.util";
import { S25Datefilter } from "../../s25-dateformat/s25.datefilter.service";
import { S25CollapseChevronComponent } from "../../s25-collapse-chevron/s25.collapse.chevron.component";
import { S25ItemComponent } from "../../s25-item/s25.item.component";
import { S25LoadingInlineModule } from "../../s25-loading/s25.loading.inline.module";
import { Range } from "../../s25-slider/s25.slider.component";
import { S25SliderModule } from "../../s25-slider/s25.slider.module";
import { ModalComponent } from "../modal.component";
import { ModalHeaderComponent } from "../modal.header.component";

export interface OccurrenceEditModalData {
    rsvId: number;
    /** Event id — used to hide Delete when this is the event's only occurrence. */
    eventId?: number;
    delete?: () => void | Promise<void>;
    copy?: () => void;
    removeItem?: () => void;
    action?: string;
    status?: string;
}

type RsvDetailItem = {
    itemId?: number;
    itemName?: string;
    itemTypeId?: number;
    layoutName?: string;
    instruction?: string;
    quantity?: number | string;
    isShare?: boolean;
    /** true when the item comes from the *Approval list (a request awaiting approval). */
    isPending?: boolean;
};

/**
 * One line of the collapsed summary. rsvdetail keeps assigned and awaiting-approval items in
 * separate lists, and the summary counts each list on its own line, as Event Details does.
 */
type SummaryGroup = {
    key: string;
    itemTypeId: Item.Ids.Location | Item.Ids.Resource;
    iconClass: string;
    isPending: boolean;
    items: RsvDetailItem[];
    countLabel: string;
};

@Component({
    standalone: true,
    imports: [
        ModalHeaderComponent,
        S25LoadingInlineModule,
        S25SliderModule,
        S25CollapseChevronComponent,
        S25ItemComponent,
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: { class: "occurrence-edit-modal" },
    template: `
        <s25-modal-header [data]="params()" />
        <div class="modal-body">
            @if (isLoading()) {
                <s25-ng-loading-inline-static />
            } @else if (rsv()) {
                <div class="occurrence-edit-modal__actions">
                    @if (showDelete()) {
                        <button type="button" class="btn btn-default" (click)="delete()">Delete</button>
                    }
                    <button type="button" class="btn btn-default" (click)="copy()">Copy</button>
                    @if (stateOptions()?.length) {
                        <select
                            class="cn-form__control occurrence-edit-modal__state"
                            aria-label="Occurrence State"
                            [disabled]="savingState()"
                            (change)="onStateChange($event)"
                        >
                            @for (state of stateOptions(); track state.itemId) {
                                <option [value]="state.itemId" [selected]="chosenStateId() === state.itemId">
                                    {{ state.itemName }}
                                </option>
                            }
                        </select>
                    }
                </div>

                <div class="occurrence-edit-modal__panel">
                    <div
                        class="occurrence-edit-modal__summary-row"
                        [class.occurrence-edit-modal__summary-row--expandable]="isExpandable()"
                        [attr.role]="isExpandable() ? 'button' : null"
                        [attr.tabindex]="isExpandable() ? 0 : null"
                        [attr.aria-expanded]="isExpandable() ? !isCollapsed() : null"
                        [attr.aria-label]="
                            isExpandable()
                                ? isCollapsed()
                                    ? 'Expand occurrence details'
                                    : 'Collapse occurrence details'
                                : null
                        "
                        (click)="toggleExpanded()"
                        (keydown.enter)="toggleExpanded()"
                        (keydown.space)="onSummarySpace($event)"
                    >
                        <div class="occurrence-edit-modal__col occurrence-edit-modal__col-date">
                            {{ formattedDate() }}
                        </div>
                        <div class="occurrence-edit-modal__col occurrence-edit-modal__col-time">
                            {{ formattedStartTime() }}
                        </div>
                        <div class="occurrence-edit-modal__col occurrence-edit-modal__col-time">
                            {{ formattedEndTime() }}
                        </div>
                        <div class="occurrence-edit-modal__col occurrence-edit-modal__col-objects">
                            @for (group of summaryGroups(); track group.key) {
                                <div class="occurrence-edit-modal__summary-object">
                                    @if (group.items.length === 1) {
                                        @let item = group.items[0];
                                        <s25-item
                                            [class.occurrence-edit-modal__object-name--pending]="group.isPending"
                                            [itemId]="item.itemId!"
                                            [itemTypeId]="group.itemTypeId"
                                            [itemName]="item.itemName"
                                            [includeTypeIcon]="true"
                                            [typeIconClass]="group.iconClass"
                                        />
                                        @if (item.quantity != null && item.quantity !== "") {
                                            <span>[{{ item.quantity }}]</span>
                                        }
                                        @if (group.isPending) {
                                            <span class="occurrence-edit-modal__object-pending">
                                                is awaiting approval
                                            </span>
                                        }
                                    } @else {
                                        <div class="s25-item-type-icon-holder">
                                            <div class="{{ group.iconClass }} s25-item-type-icon"></div>
                                        </div>
                                        <span [class.occurrence-edit-modal__object-pending]="group.isPending">
                                            {{ group.countLabel }}
                                        </span>
                                    }
                                </div>
                            }
                        </div>
                        @if (isExpandable()) {
                            <div class="occurrence-edit-modal__col occurrence-edit-modal__col-toggle">
                                <s25-ng-collapse-chevron [expanded]="!isCollapsed()" />
                            </div>
                        }
                    </div>

                    @if (isExpandable() && !isCollapsed()) {
                        <div class="occurrence-edit-modal__expanded">
                            @if (sliderModel(); as model) {
                                <div class="occurrence-edit-modal__slider">
                                    <s25-ng-slider [model]="model" [readonly]="true" />
                                </div>
                            }

                            @if (rsv()?.comment) {
                                <div class="ngPadTop10">
                                    <div class="ngBold">Comment</div>
                                    <div>{{ rsv()?.comment }}</div>
                                </div>
                            }

                            @if (spaces().length || resources().length) {
                                <div class="occurrence-edit-modal__objects ngPadTop10">
                                    @if (spaces().length) {
                                        <div class="occurrence-edit-modal__object-col">
                                            @for (space of spaces(); track $index) {
                                                <div class="occurrence-edit-modal__object">
                                                    <div class="occurrence-edit-modal__object-row">
                                                        <div class="occurrence-edit-modal__object-body">
                                                            <span class="occurrence-edit-modal__object-name-line">
                                                                <s25-item
                                                                    class="occurrence-edit-modal__object-name"
                                                                    [class.occurrence-edit-modal__object-name--pending]="
                                                                        space.isPending
                                                                    "
                                                                    [itemId]="space.itemId!"
                                                                    [itemTypeId]="Item.Ids.Location"
                                                                    [itemName]="space.itemName"
                                                                    [includeTypeIcon]="true"
                                                                    [typeIconClass]="
                                                                        space.isPending ? 'icon-space-pending' : ''
                                                                    "
                                                                />
                                                                @if (space.isPending) {
                                                                    <span class="occurrence-edit-modal__object-pending">
                                                                        is awaiting approval
                                                                    </span>
                                                                }
                                                            </span>
                                                            @if (space.isShare) {
                                                                <div class="occurrence-edit-modal__object-meta">
                                                                    Sharing: This location can be shared during this
                                                                    occurrence
                                                                </div>
                                                            }
                                                            @if (space.layoutName) {
                                                                <div class="occurrence-edit-modal__object-meta">
                                                                    Layout: {{ space.layoutName }}
                                                                </div>
                                                            }
                                                            @if (space.instruction) {
                                                                <div class="occurrence-edit-modal__object-meta">
                                                                    Instructions: {{ space.instruction }}
                                                                </div>
                                                            }
                                                        </div>
                                                    </div>
                                                </div>
                                            }
                                        </div>
                                    }
                                    @if (resources().length) {
                                        <div class="occurrence-edit-modal__object-col">
                                            @for (resource of resources(); track $index) {
                                                <div class="occurrence-edit-modal__object">
                                                    <div class="occurrence-edit-modal__object-row">
                                                        <div class="occurrence-edit-modal__object-body">
                                                            <div class="occurrence-edit-modal__object-name-row">
                                                                <span class="occurrence-edit-modal__object-name-line">
                                                                    <s25-item
                                                                        class="occurrence-edit-modal__object-name"
                                                                        [class.occurrence-edit-modal__object-name--pending]="
                                                                            resource.isPending
                                                                        "
                                                                        [itemId]="resource.itemId!"
                                                                        [itemTypeId]="Item.Ids.Resource"
                                                                        [itemName]="resource.itemName"
                                                                        [includeTypeIcon]="true"
                                                                        [typeIconClass]="
                                                                            resource.isPending
                                                                                ? 'icon-resource-pending'
                                                                                : ''
                                                                        "
                                                                    />
                                                                    @if (resource.isPending) {
                                                                        <span
                                                                            class="occurrence-edit-modal__object-pending"
                                                                        >
                                                                            is awaiting approval
                                                                        </span>
                                                                    }
                                                                </span>
                                                                @if (
                                                                    resource.quantity != null &&
                                                                    resource.quantity !== ""
                                                                ) {
                                                                    <div class="occurrence-edit-modal__object-quantity">
                                                                        [Quantity: {{ resource.quantity }}]
                                                                    </div>
                                                                }
                                                            </div>
                                                            @if (resource.instruction) {
                                                                <div class="occurrence-edit-modal__object-meta">
                                                                    Instructions: {{ resource.instruction }}
                                                                </div>
                                                            }
                                                        </div>
                                                    </div>
                                                </div>
                                            }
                                        </div>
                                    }
                                </div>
                            }
                        </div>
                    }
                </div>
            }
        </div>
        <div class="modal-footer"></div>
    `,
    styles: `
        .occurrence-edit-modal__actions {
            display: flex;
            flex-wrap: wrap;
            align-items: center;
            gap: 0.5rem;
        }

        .occurrence-edit-modal__state {
            width: auto;
            min-width: 10rem;
            display: inline-block;
        }

        .occurrence-edit-modal__panel {
            margin-top: 10px;
            margin-bottom: 10px;
            border: 1px solid var(--color--border-bold);
            background-color: var(--color--surface-2-bg);
            overflow: visible;
        }

        .occurrence-edit-modal__summary-row {
            display: flex;
            align-items: flex-start;
            gap: 1rem;
            width: 100%;
            padding: 0.6rem 0.75rem;
            box-sizing: border-box;
        }

        .occurrence-edit-modal__summary-row--expandable {
            cursor: pointer;
        }

        .occurrence-edit-modal__col {
            min-width: 0;
        }

        .occurrence-edit-modal__col-date {
            flex: 0 0 9.5rem;
            font-weight: 600;
        }

        .occurrence-edit-modal__col-time {
            flex: 0 0 5.5rem;
        }

        .occurrence-edit-modal__col-objects {
            flex: 1 1 auto;
        }

        .occurrence-edit-modal__col-toggle {
            flex: 0 0 auto;
            display: flex;
            align-items: flex-start;
            padding-top: 0.1rem;
        }

        .occurrence-edit-modal__summary-object {
            display: flex;
            align-items: center;
            gap: 0.35rem;
            margin-bottom: 0.15rem;
        }

        .occurrence-edit-modal__expanded {
            padding: 0.75rem;
            border-top: 1px solid var(--color--border);
            overflow: visible;
        }

        .occurrence-edit-modal__slider {
            padding-inline: 4.5em;
            overflow: visible;
        }

        .occurrence-edit-modal__objects {
            display: flex;
            gap: 1.5rem;
            align-items: flex-start;
        }

        .occurrence-edit-modal__object-col {
            flex: 1 1 0;
            min-width: 0;
        }

        .occurrence-edit-modal__object {
            margin-bottom: 0.75rem;
        }

        .occurrence-edit-modal__object-row {
            display: flex;
            align-items: flex-start;
            gap: 0.5rem;
        }

        .occurrence-edit-modal__object-body {
            flex: 1 1 auto;
            min-width: 0;
        }

        .occurrence-edit-modal__object-name-row {
            display: flex;
            align-items: baseline;
            justify-content: space-between;
            gap: 0.75rem;
        }

        .occurrence-edit-modal__object-name-line {
            display: inline-flex;
            align-items: baseline;
            gap: 0.35rem;
            min-width: 0;
        }

        .occurrence-edit-modal__object-name {
            font-weight: 600;
            min-width: 0;
        }

        /* Pending (awaiting-approval) requests show a muted name to match the greyed pending icon. */
        .occurrence-edit-modal__object-name--pending ::ng-deep .s25-item-name {
            color: var(--color--text-muted);
        }

        .occurrence-edit-modal__object-pending {
            font-weight: 400;
            font-style: italic;
            color: var(--color--text-muted);
        }

        .occurrence-edit-modal__object-quantity {
            flex: 0 0 auto;
            font-weight: 400;
            text-align: right;
            color: var(--color--text-muted);
        }

        .occurrence-edit-modal__object-meta {
            margin-top: 0.15rem;
            padding-left: 1.5rem;
        }
    `,
})
export class OccurrenceEditModal extends ModalComponent<OccurrenceEditModalData> {
    static readonly size = "lg" as const;
    static readonly title = "Occurrence Details";
    static readonly asDialog = true;

    constructor() {
        super();
        TelemetryService.send("Avail", "OccDetails");
    }

    protected readonly Item = Item;
    readonly savingState = signal(false);
    readonly isCollapsed = signal(false);

    readonly rsvResource = s25Resource({
        if: () => !!this.params()?.rsvId,
        loader: () => ReservationService.getRsvdetail(this.params()!.rsvId),
    });

    readonly stateResource = s25Resource({
        if: () => !!this.params()?.rsvId,
        loader: () => ReservationService.getReservationState(this.params()!.rsvId),
    });

    readonly prefsResource = s25Resource({
        loader: async () => ({
            dateFormat: await UserprefService.getS25Dateformat(),
            timeFormat: await UserprefService.getS25Timeformat(),
        }),
    });

    readonly stateOptionsResource = s25Resource({
        if: () => this.stateResource.value() != null,
        params: () => ({ state: this.stateResource.value()! }),
        loader: ({ params }) => S25ReservationUtil.getRsrvState(params.state as Event.Reservation.States),
    });

    readonly eventResource = s25Resource({
        if: () => !!this.params()?.eventId,
        params: () => this.params()!.eventId!,
        loader: ({ params: eventId }) => EventService.getEventInclude(eventId, ["reservations"]),
    });

    readonly rsv = computed(() => {
        const detail = this.rsvResource.value()?.rsvdetail?.rsv;
        if (!detail) {
            return detail;
        }
        // Drop the system-timezone offset so times render in the event/system wall-clock
        // (matching the availability grid), not shifted into the browser's timezone.
        return {
            ...detail,
            startDt: this.parseWallClock(detail.startDt),
            endDt: this.parseWallClock(detail.endDt),
            preDt: this.parseWallClock(detail.preDt),
            postDt: this.parseWallClock(detail.postDt),
            setupDt: this.parseWallClock(detail.setupDt),
            takedownDt: this.parseWallClock(detail.takedownDt),
        };
    });
    readonly stateOptions = computed(() => this.stateOptionsResource.value());
    readonly chosenStateId = linkedSignal(
        () => this.stateResource.value() ?? (Event.Reservation.States.Active as number),
    );

    /** Same idea as Event Details `singleRsrv`: hide Delete when this is the only occurrence. */
    readonly showDelete = computed(() => {
        if (!this.params()?.eventId) {
            return true;
        }
        const eventData = this.eventResource.value();
        if (!eventData) {
            return false;
        }
        return EventService.extractReservations(eventData).length > 1;
    });

    readonly isLoading = computed(
        () =>
            (this.rsvResource.isLoading() && !this.rsvResource.lastValue()) ||
            (this.stateResource.isLoading() && this.stateResource.value() == null) ||
            this.prefsResource.isLoading(),
    );

    /**
     * Unlike the Event Details occurrences pane, the expanded area always adds the time bar, so it
     * is offered whenever anything below the summary row would render. Cancelled occurrences keep
     * the legacy behavior of never expanding.
     */
    readonly isExpandable = computed(() => {
        const detail = this.rsv();
        if (!detail || detail.isCancelled) {
            return false;
        }
        return !!(this.sliderRanges().length || detail.comment || this.spaces().length || this.resources().length);
    });

    readonly formattedDate = computed(() => {
        const startDt = this.rsv()?.startDt;
        const dateFormat = this.prefsResource.value()?.dateFormat;
        if (!startDt || !dateFormat) {
            return "";
        }
        return S25Datefilter.transform(startDt, dateFormat);
    });

    readonly formattedStartTime = computed(() => this.formatTime(this.rsv()?.startDt));
    readonly formattedEndTime = computed(() => this.formatTime(this.rsv()?.endDt));

    readonly assignedSpaces = computed(() => this.toItemList(this.rsv()?.space, false));
    readonly pendingSpaces = computed(() => this.toItemList(this.rsv()?.spaceApproval, true));
    readonly assignedResources = computed(() => this.toItemList(this.rsv()?.res, false));
    readonly pendingResources = computed(() => this.toItemList(this.rsv()?.resApproval, true));

    readonly spaces = computed(() => [...this.assignedSpaces(), ...this.pendingSpaces()]);
    readonly resources = computed(() => [...this.assignedResources(), ...this.pendingResources()]);

    readonly summaryGroups = computed((): SummaryGroup[] =>
        [
            this.toSummaryGroup("space", Item.Ids.Location, false, this.assignedSpaces()),
            this.toSummaryGroup("spaceApproval", Item.Ids.Location, true, this.pendingSpaces()),
            this.toSummaryGroup("res", Item.Ids.Resource, false, this.assignedResources()),
            this.toSummaryGroup("resApproval", Item.Ids.Resource, true, this.pendingResources()),
        ].filter((group) => group.items.length),
    );

    readonly sliderRanges = computed((): Range[] => {
        const detail = this.rsv();
        if (!detail?.startDt || !detail?.endDt) {
            return [];
        }

        const timeLabels = OccurrenceTimeRangeLabels;
        const ranges: Range[] = [
            {
                start: detail.startDt,
                end: detail.endDt,
                startLabel: timeLabels.event.start,
                endLabel: timeLabels.event.end,
                hideStart: false,
                hideEnd: false,
            },
        ];

        if (detail.preDt || detail.postDt) {
            ranges.push({
                start: detail.preDt || detail.startDt,
                end: detail.postDt || detail.endDt,
                startLabel: timeLabels.prePost.start,
                endLabel: timeLabels.prePost.end,
                hideStart: !detail.preDt || this.isSameTime(detail.preDt, detail.startDt),
                hideEnd: !detail.postDt || this.isSameTime(detail.postDt, detail.endDt),
            });
        }

        if (detail.setupDt || detail.takedownDt) {
            ranges.push({
                start: detail.setupDt || detail.preDt || detail.startDt,
                end: detail.takedownDt || detail.postDt || detail.endDt,
                startLabel: timeLabels.setupTakedown.start,
                endLabel: timeLabels.setupTakedown.end,
                hideStart: !detail.setupDt || this.isSameTime(detail.setupDt, detail.startDt),
                hideEnd: !detail.takedownDt || this.isSameTime(detail.takedownDt, detail.endDt),
            });
        }

        return ranges;
    });

    /** Fresh copy on each expand — s25-ng-slider mutates ranges in ngOnInit. */
    readonly sliderModel = computed(() => {
        if (this.isCollapsed()) {
            return undefined;
        }
        const ranges = this.sliderRanges();
        if (!ranges.length) {
            return undefined;
        }
        return {
            type: "time" as const,
            ranges: S25Util.deepCopy(ranges),
            step: 0.25,
        };
    });

    toggleExpanded(): void {
        if (!this.isExpandable()) {
            return;
        }
        this.isCollapsed.update((collapsed) => !collapsed);
    }

    onSummarySpace(domEvent: Event): void {
        domEvent.preventDefault();
        this.toggleExpanded();
    }

    delete(): void {
        const params = this.params();
        if (!params) {
            return;
        }
        if (params.delete) {
            void params.delete();
        }
    }

    copy(): void {
        const params = this.params();
        if (!params) {
            return;
        }
        if (params.copy) {
            params.copy();
            return;
        }
        this.completeAction("copy", "success");
    }

    async onStateChange(domEvent: UIEvent): Promise<void> {
        const select = domEvent.target as HTMLSelectElement | null;
        const newStateId = S25Util.parseInt(select?.value);
        const previousStateId = this.chosenStateId();
        if (!Number.isFinite(newStateId) || newStateId === previousStateId || this.savingState()) {
            return;
        }

        const rsvId = this.params()?.rsvId;
        if (rsvId == null) {
            return;
        }

        this.chosenStateId.set(newStateId);
        this.savingState.set(true);
        try {
            const response = await AvailService.setReservationState(rsvId, newStateId);
            if (response?.ret === 1) {
                CacheRepository.invalidateByObject("ReservationService", "getRsvdetail", [rsvId]);
                this.handleStateChanged(newStateId);
                return;
            }
            this.chosenStateId.set(previousStateId);
            if (response?.msg) {
                AlertService.alert(response.msg);
            }
        } catch (caughtError) {
            this.chosenStateId.set(previousStateId);
            S25Util.showError(
                caughtError,
                "There was an error while processing the reservation state change. Please contact your administrator.",
            );
        } finally {
            this.savingState.set(false);
        }
    }

    private handleStateChanged(newStateId: number): void {
        if (
            [Event.Reservation.States.Exception, Event.Reservation.States.Cancelled].includes(
                newStateId as Event.Reservation.States,
            )
        ) {
            const params = this.params();
            if (params?.removeItem) {
                params.removeItem();
            } else {
                params?.closeModal();
            }
            return;
        }
        void this.stateResource.reloadAndAwait();
        void this.rsvResource.reloadAndAwait();
    }

    private formatTime(date: Date | undefined): string {
        const timeFormat = this.prefsResource.value()?.timeFormat;
        if (!date || !timeFormat) {
            return "";
        }
        return S25Datefilter.transform(date, timeFormat);
    }

    private isSameTime(left: Date, right: Date): boolean {
        return left?.getTime?.() === right?.getTime?.();
    }

    /** Parse a rsvdetail date string as wall-clock (offset dropped), returning undefined when absent. */
    private parseWallClock(value: unknown): Date | undefined {
        return value ? S25Util.date.parseDropTZ(value) : undefined;
    }

    /**
     * rsvdetail repeats a space/resource once per approval requirement, and each repeat can carry
     * its own instruction, so every entry is kept as its own row.
     */
    private toItemList(items: RsvDetailItem[] | undefined, isPending: boolean): RsvDetailItem[] {
        return (items ?? []).filter((item) => !!item?.itemId).map((item) => ({ ...item, isPending }));
    }

    private toSummaryGroup(
        key: string,
        itemTypeId: Item.Ids.Location | Item.Ids.Resource,
        isPending: boolean,
        items: RsvDetailItem[],
    ): SummaryGroup {
        const isLocation = itemTypeId === Item.Ids.Location;
        const iconType = isLocation ? "space" : "resource";
        const noun = isLocation ? "location" : "resource";
        const plural = items.length === 1 ? noun : `${noun}s`;
        return {
            key,
            itemTypeId,
            iconClass: isPending ? `icon-${iconType}-pending` : `icon-${iconType}`,
            isPending,
            items,
            countLabel: `(${items.length} ${plural}${isPending ? " awaiting approval" : ""})`,
        };
    }

    private completeAction(action: string, status: string): void {
        const params = this.params();
        if (!params) {
            return;
        }
        params.action = action;
        params.status = status;
        params.closeModal();
    }
}
